import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, IconButton,
    Modal, ModalDialog, ModalClose, Input, Select, Option, Alert,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon  from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon        from '@mui/icons-material/AddRounded';
import EditRoundedIcon       from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon     from '@mui/icons-material/DeleteRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetPaymentNumbers, APICreatePaymentNumber,
    APIUpdatePaymentNumber, APIDeletePaymentNumber,
    APIGetLocations, parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import { BANKS, BankBadge, formatPhone } from '../../components/BankBrand';


const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white', flex: 1,
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
    "&:hover": {
        bgcolor: 'var(--joy-palette-background-level3)', 
    }
};


function PaymentNumberModal({ open, item, locations, onClose, onSave, loading, error }) {
    const isEdit = !!item;
    const [form, setForm] = useState({
        phone: '', bank: 'alpha', owner_name: '',
        location_id: null, comment: '', is_active: true,
    });

    useEffect(() => {
        if (open) {
            if (item) {
                setForm({
                    phone:        item.phone || '',
                    bank:         item.bank || 'alpha',
                    owner_name:   item.owner_name || '',
                    location_id:  item.location || null,
                    comment:      item.comment || '',
                    is_active:    item.is_active ?? true,
                });
            } else {
                setForm({ phone: '', bank: 'alpha', owner_name: '', location_id: null, comment: '', is_active: true });
            }
        }
    }, [open, item]);

    const set = field => e => {
        const val = typeof e === 'string' ? e : e.target.value;
        setForm(p => ({ ...p, [field]: val }));
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.55)' } } }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 440, width: '100%', p: 3,
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
                    {isEdit ? 'Редактировать номер' : 'Новый номер'}
                </Typography>

                <Stack spacing={1.8}>
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Телефон *
                        </Typography>
                        <Input
                            value={form.phone}
                            onChange={set('phone')}
                            placeholder="+7 999 077 77 77"
                            sx={inputSx}
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Банк *
                        </Typography>
                        <Select
                            value={form.bank}
                            onChange={(_, val) => val && setForm(p => ({ ...p, bank: val }))}
                            sx={inputSx}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            {Object.entries(BANKS).map(([code, meta]) => (
                                <Option key={code} value={code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BankBadge bank={code} size={18} />
                                        {meta.name}
                                    </Box>
                                </Option>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Имя владельца *
                        </Typography>
                        <Input
                            value={form.owner_name}
                            onChange={set('owner_name')}
                            placeholder="Иван И."
                            sx={inputSx}
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Точка (опционально)
                        </Typography>
                        <Select
                            value={form.location_id ?? ''}
                            onChange={(_, val) => setForm(p => ({ ...p, location_id: val === '' ? null : val }))}
                            placeholder="Доступен на всех точках"
                            sx={inputSx}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <Option value="">Доступен на всех точках</Option>
                            {locations.map(l => (
                                <Option key={l.id} value={l.id}>{l.name}</Option>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Комментарий
                        </Typography>
                        <Input
                            value={form.comment}
                            onChange={set('comment')}
                            placeholder="..."
                            sx={inputSx}
                        />
                    </Box>

                    {error && (
                        <Alert color="danger" variant="soft" size="sm">
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
                        <Button
                            onClick={() => onSave(form)}
                            loading={loading}
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700,
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                            }}
                        >
                            {isEdit ? 'Сохранить' : 'Создать'}
                        </Button>
                        <Button
                            variant="outlined" onClick={onClose}
                            sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)' }}
                        >
                            Отмена
                        </Button>
                    </Box>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


export default function PaymentNumbersPage() {
    const { token, showToast, hasPerm, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    const canManage = hasPerm('payment_numbers.manage');

    const [numbers,   setNumbers]   = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [editModal, setEditModal] = useState({ open: false, item: null, loading: false, error: '' });
    const [deleting,  setDeleting]  = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        const [nums, locs] = await Promise.all([
            APIGetPaymentNumbers(token),
            APIGetLocations(token),
        ]);
        if (Array.isArray(nums)) setNumbers(nums);
        if (Array.isArray(locs)) setLocations(locs);
        setLoading(false);
    }, [token]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    const handleSave = async (form) => {
        setEditModal(p => ({ ...p, loading: true, error: '' }));
        const payload = { ...form };
        const isEdit = !!editModal.item;
        const res = isEdit
            ? await APIUpdatePaymentNumber(token, editModal.item.id, payload)
            : await APICreatePaymentNumber(token, payload);
        if (res?.error) {
            setEditModal(p => ({ ...p, loading: false, error: parseApiError(res.error) }));
            return;
        }
        showToast(isEdit ? 'Номер обновлён ✓' : 'Номер добавлен ✓');
        setEditModal({ open: false, item: null, loading: false, error: '' });
        loadAll();
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Удалить номер ${formatPhone(item.phone)}?`)) return;
        setDeleting(item.id);
        const res = await APIDeletePaymentNumber(token, item.id);
        setDeleting(null);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось удалить'), 'danger');
            return;
        }
        showToast('Номер удалён', 'warning');
        setNumbers(prev => prev.filter(n => n.id !== item.id));
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />
            <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton variant="plain" onClick={() => navigate('/manager')} sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                            <ArrowBackRoundedIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                                Номера для оплаты
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Пул номеров, которые фотографы получают для приёма переводов
                            </Typography>
                        </Box>
                    </Box>
                    {canManage && (
                        <Button
                            startDecorator={<AddRoundedIcon />}
                            onClick={() => setEditModal({ open: true, item: null, loading: false, error: '' })}
                            sx={{
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700,
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                            }}
                        >
                            Добавить номер
                        </Button>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <MemeLoader />
                    </Box>
                ) : numbers.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 8,
                        borderRadius: 'var(--joy-radius-xl)',
                        border: '1px dashed var(--joy-palette-divider)',
                        bgcolor: 'var(--joy-palette-background-level1)',
                    }}>
                        <PhoneIphoneRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                            Пул номеров пуст. Добавь хотя бы один — фотографы смогут получать его для приёма переводов.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {numbers.map(n => {
                            const meta = BANKS[n.bank] || BANKS.other;
                            return (
                                <Box key={n.id} sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderRadius: 'var(--joy-radius-lg)',
                                    p: 1.8,
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    opacity: n.is_active ? 1 : 0.55,
                                }}>
                                    <BankBadge bank={n.bank} size={42} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--joy-palette-text-primary)', fontFamily: 'monospace' }}>
                                            {n.phone_formatted || formatPhone(n.phone)}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                            {meta.name} · {n.owner_name}
                                            {n.location_name && (
                                                <span style={{ color: 'var(--joy-palette-text-tertiary)' }}> · {n.location_name}</span>
                                            )}
                                        </Typography>
                                    </Box>
                                    {n.is_currently_claimed && (
                                        <Chip size="sm" sx={{
                                            bgcolor: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                                            fontWeight: 700, fontSize: 10,
                                        }}>
                                            Сейчас выдан
                                        </Chip>
                                    )}
                                    {!n.is_active && (
                                        <Chip size="sm" sx={{
                                            bgcolor: 'rgba(248,113,113,0.12)', color: '#f87171',
                                            fontWeight: 700, fontSize: 10,
                                        }}>
                                            Отключён
                                        </Chip>
                                    )}
                                    {canManage && (
                                        <>
                                            <IconButton size="sm" onClick={() => setEditModal({ open: true, item: n, loading: false, error: '' })}>
                                                <EditRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-300)' }} />
                                            </IconButton>
                                            <IconButton size="sm" disabled={deleting === n.id} onClick={() => handleDelete(n)}>
                                                <DeleteRoundedIcon sx={{ fontSize: 18, color: '#f87171' }} />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            <PaymentNumberModal
                open={editModal.open}
                item={editModal.item}
                locations={locations}
                loading={editModal.loading}
                error={editModal.error}
                onClose={() => setEditModal({ open: false, item: null, loading: false, error: '' })}
                onSave={handleSave}
            />
        </motion.div>
    );
}